import { Card } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import CustomTable from "../../components/CustomTable/CustomTable";
import { useEffect, useState } from "react";
import WebService from "../../utility/WebService";
import HelperService from "../../utility/HelperService";
import AddSubscription from "./AddManualSubscription";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import ViewSubscriptionDetails from "./ViewSubscriptionDetail";
import { EyeFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";

const SubscriptionList = () => {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [activeTab, setActiveTab] = useState<
    "ACTIVE" | "PENDING" | "EXPIRED" | "DELETED"
  >("ACTIVE");

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState<any>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [editData, setEditData] = useState<any>(null);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const sortFieldMap: Record<string, string> = {
    orderId: "orderId",
    organization: "organization",
    user: "user",
    userEmail: "email",
    planName: "planName",
    amount: "amount",
    startDate: "startDate",
    endDate: "endDate",
    paymentStatus: "paymentStatus",
  };
  const renderPaymentStatus = (status?: string) => {
    if (!status) return "-";

    const isPaid = status.toUpperCase() === "PAID";

    return (
      <span
        className={`badge ${isPaid ? "bg-success" : "bg-danger"}`}
        style={{ fontSize: "0.75rem" }}
      >
        {status}
      </span>
    );
  };
  const fetchSubscriptions = () => {
    const statusFilter = activeTab;
    const params = new URLSearchParams();
    params.append("page", String(currentPage));
    params.append("limit", String(itemsPerPage));
    params.append("q", search);
    params.append("paymentStatus", statusFilter);
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);

    WebService.getAPI({
      action: `userSubscriptions/get-list?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        const rows = res?.data?.map((item: any) => ({
          _id: item?._id,
          orderId: item?.transaction?.orderNo || "-",
          organization:
            item?.organization?.type === "ORGANIZATION"
              ? item?.organization?.name
              : "-",
          user: item?.user?.fullName || item?.userId?.email || "-",
          userEmail: item?.user?.email || item?.userId?.email || "-",
          planName: item?.plan?.planName || "-",
          amount: item?.transaction?.totalAmount || "-",
          startDate: HelperService.dateFormatter(item?.startDate),
          endDate: HelperService.dateFormatter(item?.endDate),
          // paymentMethod: item?.transaction?.paymentMethod || "-",
          paymentStatus: renderPaymentStatus(item?.transaction?.status) || "-",
          // Status: item?.status || "-",

          action: (
            <div className="d-flex justify-content-center align-items-center gap-2">
              {activeTab !== "DELETED" && perm.canEdit && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </button>
              )}

              {perm.canEdit && (
                <EyeFill
                  size={18}
                  className="text-primary cursor-pointer"
                  title="View Details"
                  onClick={() => {
                    setViewData(item);
                    setShowView(true);
                  }}
                />
              )}

              {activeTab !== "DELETED" && perm.canDelete && (
                <TrashFill
                  size={18}
                  className="text-danger cursor-pointer"
                  title="Delete"
                  onClick={() => handleDeleteSubscription(item._id)}
                />
              )}
            </div>
          ),

        }));

        setSubscriptions(rows || []);
        setTotalCount(res?.pagination?.total || 0);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, search, activeTab, sortField, sortOrder]);

  const handleEdit = (item: any) => {
    setEditData(item);
    setShowAddModal(true);
  };

  const handleDeleteSubscription = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  return (
    <>
      <AddSubscription
        show={showAddModal}
        data={editData}
        activeTab={activeTab}
        handleClose={() => {
          setShowAddModal(false);
          setEditData(null);
          fetchSubscriptions();
        }}
      />

      <ViewSubscriptionDetails
        show={showView}
        data={viewData}
        handleClose={() => {
          setShowView(false);
          setViewData(null);
        }}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteId(null);
        }}
        onDelete={() => {
          if (!deleteId) return;

          WebService.deleteAPI({
            action: `userSubscriptions/delete/${deleteId}`,
          })
            .then(() => {
              setShowDeleteModal(false);
              setDeleteId(null);
              fetchSubscriptions();
            })
            .catch(() => {
              console.error("Failed to delete subscription");
            });
        }}
        message="Are you sure you want to delete this subscription?"
      />

      <div className="events-main">
        <Card className="event-card p-3">
          {/* TITLE + SEARCH + ADD */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="page-title">Subscriptions</div>

            <div className="d-flex align-items-center gap-2">
              {pagePermission.canAdd && (
                <CustomButton
                  isLoading={false}
                  label="Add"
                  onClick={() => {
                    setEditData(null);
                    setShowAddModal(true);
                  }}
                  style={{ width: 100 }}
                />
              )}
            </div>
          </div>
          <div className="d-flex mb-1 align-items-center">
            <input
              type="text"
              placeholder="Search user / user email / org..."
              className="form-control"
              style={{ width: "250px" }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* TABS BELOW TITLE */}
          <div className="d-flex gap-4 mb-3">
            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "ACTIVE"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "ACTIVE" ? "#007bff" : "#555",
                fontWeight: activeTab === "ACTIVE" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("ACTIVE");
                setCurrentPage(1);
              }}
            >
              Active
            </div>

            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "PENDING"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "PENDING" ? "#007bff" : "#555",
                fontWeight: activeTab === "PENDING" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("PENDING");
                setCurrentPage(1);
              }}
            >
              Pending
            </div>

            {/* EXPIRED */}
            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "EXPIRED"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "EXPIRED" ? "#007bff" : "#555",
                fontWeight: activeTab === "EXPIRED" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("EXPIRED");
                setCurrentPage(1);
              }}
            >
              Expired
            </div>

            {/* DELETED */}
            <div
              style={{
                cursor: "pointer",
                paddingBottom: "6px",
                borderBottom:
                  activeTab === "DELETED"
                    ? "2px solid #007bff"
                    : "2px solid transparent",
                color: activeTab === "DELETED" ? "#007bff" : "#555",
                fontWeight: activeTab === "DELETED" ? "600" : "400",
              }}
              onClick={() => {
                setActiveTab("DELETED");
                setCurrentPage(1);
              }}
            >
              Deleted
            </div>
          </div>

          {/* TABLE */}
          <CustomTableSortable
            columns={[
              {
                id: "orderId",
                label: "Subscription id",
                minWidth: 150,
                sortable: true,
              },
              {
                id: "organization",
                label: "Organization",
                minWidth: 150,
                sortable: true,
              },
              { id: "user", label: "User", minWidth: 150, sortable: true },
              { id: "userEmail", label: "Primary User Email", minWidth: 150, sortable: true },
              {
                id: "planName",
                label: "Plan Name",
                minWidth: 150,
                sortable: true,
              },
              { id: "amount", label: "Amount", minWidth: 150, sortable: true },
              {
                id: "startDate",
                label: "Start Date",
                minWidth: 120,
                align: "center",
                sortable: true,
              },
              {
                id: "endDate",
                label: "End Date",
                minWidth: 120,
                align: "center",
                sortable: true,
              },
              // {
              //   id: "paymentMethod",
              //   label: "Payment Method",
              //   minWidth: 120,
              //   align: "center",
              //   sortable: true,
              // },
              {
                id: "paymentStatus",
                label: "Payment Status",
                minWidth: 120,
                align: "center",
                sortable: true,
              },
              // { id: "Status", label: "Status", minWidth: 120, align: "center" }, // as client said to remove this
              { id: "action", label: "Action", minWidth: 120, align: "center" },
            ]}
            rows={subscriptions}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default SubscriptionList;
